import {
    SolidFormResponse,
    SolidgatePlan,
    SolidUpdateCardFormResponse,
    UpdateCardStatusResponse,
    TruegateChargeResponse
} from "../entities/payment";
import type { RestoreSubscriptionResponse } from "@features/subscription/restore-outcome";
import type { RestorePreview } from "@features/subscription/restore-confirmation";
import {
    CheckPurchasedProductsResponse,
    ISubscription,
    PauseSubscriptionRequest,
    PauseSubscriptionResponse,
    PurchaseUpsellAppleResponse,
    PurchaseUpsellRequest,
    PurchaseUpsellResponse,
    UpsellPlacement
} from "../entities/subscription";
import { withContentParams } from "../locale";
import { HttpRequest, IHttpClient } from "../request/model";
import { getAdConsent } from "@utils/cookieConsent";

const SERVER_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

interface IGetUpdateSubscriptionOptions {
    data: SolidgatePlan[];
}

export interface ICheckUpdateSubscriptionResponse {
    status: string;
}

export type SolidgateUpsellApmFormRequest = {
    productId: string;
    currency: "INR" | "BRL";
    platform: "WEB" | "MOB";
    purchasePageURL: string;
    placement: UpsellPlacement;
};

export type SolidgateUpsellPaymentCapability = {
    hasRecurringToken: boolean;
    /** The subscription's Solidgate payment_type ("card", "upi", "pix-automatico", ...). */
    paymentType?: string;
};

export class TSubscriptionApi {
    constructor(private httpClient: IHttpClient) {}

    async getSubscriptions() {
        return await this.httpClient.do<ISubscription[]>(
            withContentParams(`${SERVER_URL}/payments/subscriptions`)
        );
    }

    async checkAnySubscriptionByEmail(email: string) {
        return await this.httpClient.do<{ subscriber: boolean }>(
            `${SERVER_URL}/auth/check-subscriber?email=${encodeURIComponent(email)}`
        );
    }

    async getUpdateSubscriptionOptions() {
        // Returns full plan entities: the content locale selects the
        // admin-authored plan names (plan_metadata) like the plan catalogs.
        const response = await this.httpClient.do<IGetUpdateSubscriptionOptions>(
            withContentParams(`${SERVER_URL}/subscription/available-transitions`)
        );

        return response?.data || [];
    }

    async checkSubscriptionUpdateSuccess(product_id: string) {
        const response = await this.httpClient.do<ICheckUpdateSubscriptionResponse>(
            `${SERVER_URL}/subscription/check-update`,
            "POST",
            { product_id }
        );

        return response?.status || "";
    }

    async updateSubscription(product_id: string) {
        return await this.httpClient.do<void>(`${SERVER_URL}/subscription/update`, "POST", { product_id });
    }

    async cancelSubscriptipn(subId: string) {
        return await this.httpClient.do(`${SERVER_URL}/payments/subscription/cancel?sub_id=${subId}`, "POST");
    }

    async pauseSubscription(body: PauseSubscriptionRequest) {
        return await this.httpClient.do<PauseSubscriptionResponse>(`${SERVER_URL}/subscription/pause`, "POST", body);
    }

    async resumePausedSubscription() {
        return await this.httpClient.do<void>(`${SERVER_URL}/subscription/pause/resume`, "POST");
    }

    async restoreSubscription(subId: string) {
        return await this.httpClient.do<RestoreSubscriptionResponse>(`${SERVER_URL}/subscription/restore`, "POST", {
            sub_id: subId
        });
    }

    // What restoring the latest Solidgate subscription would do and cost, read
    // from Solidgate at the moment of the click (see restore-confirmation.ts).
    async getRestorePreview() {
        return await this.httpClient.do<RestorePreview>(`${SERVER_URL}/subscription/restore-preview`, "GET");
    }

    async purchaseUpsellProduct(params: PurchaseUpsellRequest) {
        return await this.httpClient.do<PurchaseUpsellResponse>(
            `${SERVER_URL}/payments/solidgate/charge`,
            "POST",
            params
        );
    }

    // One-time APM upsell form: UPI Intent (INR) or Pix QR (BRL).
    async getSolidgateUpsellApmForm(method: "upi" | "pix-qr", params: SolidgateUpsellApmFormRequest) {
        const path = method === "pix-qr" ? "pix-form" : "upi-form";
        return await this.httpClient.do<SolidFormResponse>(
            `${SERVER_URL}/payments/solidgate/upsell/${path}`,
            "POST",
            params
        );
    }

    async getSolidgateUpsellPaymentCapability() {
        return await this.httpClient.do<SolidgateUpsellPaymentCapability>(
            `${SERVER_URL}/payments/solidgate/upsell/payment-capability`
        );
    }

    async purchaseUpsellProductApple(productId: string, transactionId: string, purchasePageURL: string) {
        return await this.httpClient.do<PurchaseUpsellAppleResponse>(
            `${SERVER_URL}/payments/apple/charge-upsell`,
            "POST",
            {
                productId,
                transactionId,
                purchasePageURL
            }
        );
    }

    async purchaseUpsellProductAnon(
        productId: string,
        platform: "WEB" | "MOB",
        purchasePageURL: string,
        email: string,
        currency?: string
    ) {
        return await this.httpClient.do<PurchaseUpsellResponse>(
            `${SERVER_URL}/payments/solidgate/charge-anon`,
            "POST",
            {
                productId,
                currency,
                platform,
                purchasePageURL,
                email
            }
        );
    }

    async checkUpsellPurchased(upsellIds: string[]) {
        return await this.httpClient.do<CheckPurchasedProductsResponse>(
            `${SERVER_URL}/payments/upsell/check-purchased`,
            "POST",
            { upsellIds }
        );
    }

    async purchaseTruegateUpsellProduct(planId: number, placement: UpsellPlacement) {
        return await this.httpClient.do<TruegateChargeResponse>(`${SERVER_URL}/payments/truegate/charge`, "POST", {
            planId,
            platform: "web",
            placement,
            metadata: getAdConsent()
        });
    }

    async purchaseTruegateUpsellProductAnon(planId: number, email: string, placement: UpsellPlacement) {
        return await this.httpClient.do<TruegateChargeResponse>(`${SERVER_URL}/payments/truegate/charge-anon`, "POST", {
            planId,
            email,
            platform: "web",
            placement,
            metadata: getAdConsent()
        });
    }

    async purchaseEcommpayUpsellProduct(planCode: string, placement: UpsellPlacement) {
        return await this.httpClient.do<PurchaseUpsellResponse>(`${SERVER_URL}/payments/ecomm/upsell`, "POST", {
            planCode,
            placement
        });
    }

    async purchaseEcommpayUpsellProductAnon(planCode: string, email: string) {
        return await this.httpClient.do<PurchaseUpsellResponse>(`${SERVER_URL}/payments/ecomm/upsell-anon`, "POST", {
            planCode,
            email
        });
    }

    async grantFreeUpsell() {
        return await this.httpClient.do<void>(`${SERVER_URL}/payments/upsell/grant-free`, "POST");
    }

    async revokeFreeUpsell() {
        return await this.httpClient.do<{ success: boolean; revokedCount: number }>(
            `${SERVER_URL}/payments/upsell/revoke-free`,
            "POST"
        );
    }

    async switchSubscription(product_id: string) {
        return await this.httpClient.do<void>(`${SERVER_URL}/subscription/switch`, "POST", { product_id });
    }

    async getUpdateCardForm() {
        return await this.httpClient.do<SolidUpdateCardFormResponse>(
            `${SERVER_URL}/payments/solidgate/update-card-form`
        );
    }

    async checkUpdateCardStatus(orderId: string) {
        return await this.httpClient.do<UpdateCardStatusResponse>(
            `${SERVER_URL}/payments/solidgate/check-update-card-status?order_id=${encodeURIComponent(orderId)}`
        );
    }

    async getInvoice(orderId: string): Promise<Blob> {
        return this.httpClient.doBlob(`${SERVER_URL}/payments/invoice/${orderId}`);
    }
}

export const SubscriptionApi = new TSubscriptionApi(HttpRequest);
